import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Card, Loader, Input, Label, Message, Segment, Icon } from 'semantic-ui-react';
import TargetLink from '../../components/target-link';
import { compose } from 'recompose';
import hasRESTClient from '../../hocs/has-rest-client';
import hasUserInfo from '../../hocs/has-user-info';

import InfluencerTag from '../../components/influencer-tag';

const helpUrl = 'https://help.dyzio.co/en/articles/5137000-adding-business-units-or-brands';

const helpLinkStyle = {
  color: 'rgb(32, 196, 158)',
};

const businessUnitsContainerStyle = {
  minHeight: 50,
};

const errorStyle = {
  color: '#ef3a50',
  fontSize: 12,
  fontWeight: 'bold',
};

class BusinessUnitsManager extends React.Component {
  //
  constructor() {
    super();
    this.state = {
      businessUnits: [],
      newBusinessUnitValue: '',
      isLoadingBusinessUnits: true,
      businessUnitsLoaded: false,
      isAddingBusinessUnit: false,
    };
  }

  async componentDidMount() {
    await this.onLoadBusinessUnits();
  }

  onInputChanged = (event, { value }) => {
    this.setState({
      newBusinessUnitValue: value,
    });
  }

  onLoadBusinessUnits = async () => {
    const { restClient, userInfo: { organisationId } } = this.props;
    this.setState({
      isLoadingBusinessUnits: true,
    });
    const response = await restClient.get(`businessUnits?filter[organisation]=${organisationId}`);
    const businessUnits = _.get(response, 'data.data', []);
    this.setState({
      businessUnits,
      isLoadingBusinessUnits: false,
      businessUnitsLoaded: true,
    });
  }

  onBusinessUnitSelected = businessUnitId => {
    this.setState({
      editedBusinessUnitId: businessUnitId,
    });
  }

  onAddNewBusinessUnit = () => {
    const { restClient, userInfo: { name, organisationId } } = this.props;
    const { newBusinessUnitValue } = this.state;
    this.setState({
      isAddingANewBusinessUnit: true,
    }, async () => {
      const postData = {
        attributes: {
          value: newBusinessUnitValue,
          createdBy: name,
          organisation: {
            type: 'organisations',
            id: organisationId,
          },
        },
      };
      await restClient.post('/businessUnits', {
        data: postData,
      });
      this.setState({
        isAddingANewBusinessUnit: false,
        isLoadingBusinessUnits: true,
        newBusinessUnitValue: '',
      }, this.onLoadBusinessUnits);
    });
  }

  onDeleteBusinessUnit = async businessUnitId => {
    const { restClient } = this.props;
    await restClient.delete(`businessUnits/${businessUnitId}`);
    await this.onLoadBusinessUnits();
  }

  onSaveBusinessUnit = async (businessUnitId, value) => {
    const { restClient } = this.props;
    await restClient.patch(`businessUnits/${businessUnitId}`, {
      data: {
        attributes: {
          value,
        },
      },
    });
    await this.onLoadBusinessUnits();
  }

  render() {
    const { newBusinessUnitValue,
      isAddingANewBusinessUnit,
      isLoadingBusinessUnits, businessUnits,
      businessUnitsLoaded } = this.state;
    const duplicateBusinessUnitToBeAdded = _.some(businessUnits, ['attributes.value', newBusinessUnitValue]);
    const actionProps = {
      disabled: _.isEmpty(_.trim(newBusinessUnitValue)) || duplicateBusinessUnitToBeAdded,
      icon: 'plus',
      content: 'Add business unit or brand',
      loading: isAddingANewBusinessUnit,
      onClick: this.onAddNewBusinessUnit,
    };


    return (
      <div>
        <Card fluid>
          <Card.Content>
            <p>
              Add and manage a list of business units or brands that your organisation supports.<br />
              Once this list is created, you can add multiple business units or brands to each of the influencers in
              your database.<br />
              This enables you to filter your database, based on the business unit
               - so you will only see the relevant influencers.
            </p>
          </Card.Content>
          <Card.Content extra>
            <TargetLink style={helpLinkStyle} href={helpUrl} renderIf>
              <Icon name="help" circular />
              click here for help
            </TargetLink>
          </Card.Content>
        </Card>
        <Card fluid>
          <Card.Content>
            <h3>Business Units or Brand Name</h3>
            <p>Add, edit or delete your business units or brands</p>
          </Card.Content>
          <Card.Content>

            {
              isLoadingBusinessUnits &&
              <Segment
                style={businessUnitsContainerStyle}
                basic
              >
                <Loader active content="Loading new business units..." />
              </Segment>
            }
            {
              !isLoadingBusinessUnits && (
                <Segment basic style={businessUnitsContainerStyle}>
                  {
                    businessUnitsLoaded && _.isEmpty(businessUnits) &&
                    <Message warning content="No business units created yet." />
                  }
                  <Label.Group>
                    {
                      _.map(businessUnits, businessUnit => <InfluencerTag
                        key={businessUnit.id}
                        onClick={this.onBusinessUnitSelected}
                        onSave={this.onSaveBusinessUnit}
                        onDelete={this.onDeleteBusinessUnit}
                        {...businessUnit}
                      />)
                    }
                  </Label.Group>
                </Segment>
              )
            }
          </Card.Content>
          <Card.Content>
            <Input
              fluid
              value={newBusinessUnitValue}
              action={actionProps}
              onChange={this.onInputChanged}
              placeholder="Add a new business unit or brand name"
            />
            {
              duplicateBusinessUnitToBeAdded && <span style={errorStyle}>This business unit already exists</span>
            }
          </Card.Content>
        </Card>
      </div>
    );
  }
}

BusinessUnitsManager.propTypes = {
  restClient: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired,
};

export default compose(
  hasRESTClient,
  hasUserInfo,
)(BusinessUnitsManager);



/** WEBPACK FOOTER **
 ** ./src/containers/business-units-manager/index.js
 **/